import { useState, useReducer, useCallback } from 'react';
import { parseApiError } from '@/utils/errorHandler';
import { initialState, pageReducer } from './simulationManagementReducer';
import { useSimulations } from './useSimulations';
import type { parsedApiError } from '@/types/api/errors.api';
import type { simulation } from '../dashboard/types.api';
import type { simulationFormData } from '../userManagement/components/userFormModal/types.props';

export const useSimulationManagement = () => {
    const [pageState, dispatch] = useReducer(pageReducer, initialState);
    const [searchTerm, setSearchTerm] = useState('');

    const {
        data: simulations,
        count,
        currentPage,
        filters,
        isLoading,
        isSubmitting,
        error: listError,
        setPage,
        setFilters,
        create,
        update,
        remove,
    } = useSimulations();

    const [apiFormError, setApiFormError] = useState<parsedApiError | null>(null);

    const handleAdd = useCallback(() => dispatch({ type: 'OPEN_MODAL' }), []);
    const handleEdit = useCallback((sim: simulation) => dispatch({ type: 'OPEN_MODAL', payload: sim }), []);
    const handleCloseModal = useCallback(() => dispatch({ type: 'CLOSE_MODAL' }), []);

    const handleDelete = useCallback(async (sim: simulation) => {
        if (window.confirm(`¿Seguro que quieres eliminar "${sim.name}"?`)) {
            try {
                await remove(sim.id);
            } catch (error) {
                dispatch({ type: 'SET_ACTION_ERROR', payload: parseApiError(error) });
            }
        }
    }, [remove]);

    const handleSubmit = useCallback(async (data: simulationFormData) => {
        setApiFormError(null);
        try {
            if (pageState.editingSimulation) {
                await update(pageState.editingSimulation.id, data);
            } else {
                await create(data);
            }
            setFilters({ ...filters });
            handleCloseModal();
        } catch (error) {
            setApiFormError(parseApiError(error));
        }
    }, [create, update, pageState.editingSimulation, handleCloseModal, filters, setFilters]);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setFilters({
            ...filters,
            search: value
        });
        if (pageState.actionError) dispatch({ type: 'CLEAR_ACTION_ERROR' });
    };

    const handleDateFilter = (dates: { created_at__gte: string, created_at__lte: string }) => {
        setFilters({
            ...filters,
            created_at__gte: dates.created_at__gte || undefined,
            created_at__lte: dates.created_at__lte || undefined,
        });
        if (pageState.actionError) dispatch({ type: 'CLEAR_ACTION_ERROR' });
    };

    return {
        ...pageState,
        simulations,
        count,
        currentPage,
        isLoading,
        isSubmitting,
        listError,
        apiFormError,
        searchTerm,
        setPage,
        handleAdd,
        handleEdit,
        handleDelete,
        handleSubmit,
        handleCloseModal,
        handleSearchChange,
        handleDateFilter,
    };
};
